import React, { useState, useEffect } from 'react';
import './Sidebar.css';
import { getDbStatus, resolveDbConfig } from '../config/productDatabaseMap';
import { API_BASE } from '../config/apiConfig';

// Client-side fallback terminology defaults for sidebar label resolution
const TERMINOLOGY_DEFAULTS = {
  "Restaurant.Type-1": "Food",
  "Grocery.Type-1": "Inventory",
  "Construction.Type-1": "Materials",
};

// TN Procurement canonical ProductType — sidebar items shown only for this product
const TN_PROCUREMENT_CANONICAL =
  'Procurement Intelligence.Type-1 (Prototyped for Tamil Nadu Govt)';
const TN_PROCUREMENT_SLUG = 'procurement-intelligence-type-1-tn-prototype';

// Industrial Water PFAS Intelligence canonical ProductType
const IW_PFAS_CANONICAL = 'IndustrialWater-PFAS-Intelligence.Type-2';
const IW_PFAS_SLUG = 'industrial-water-pfas-intelligence-type-1';

function isTNProcurement(productType) {
  return (
    productType === TN_PROCUREMENT_CANONICAL ||
    productType === TN_PROCUREMENT_SLUG
  );
}

function isIWPfas(productType) {
  return (
    productType === IW_PFAS_CANONICAL ||
    productType === IW_PFAS_SLUG
  );
}

function handleLogout() {
  localStorage.removeItem('pnp_access_token');
  localStorage.removeItem('pnp_id_token');
  localStorage.removeItem('pnp_refresh_token');
  localStorage.removeItem('pnp_subscriber_session');
  localStorage.removeItem('pnp_onboarded_clients');
  localStorage.removeItem('pnp_all_sessions');
  window.location.href = '/login';
}

/**
 * ClientPortalSidebar — The "Launch" view sidebar.
 *
 * Structure:
 *   Intelligence Dashboard (header)
 *     ├── Overview        → ties to "Overview" tab inside
 *     ├── LLM Ops         → ties to "LLM Ops" tab inside
 *     ├── Comm-AI         → ties to "Comm-AI" tab inside
 *     └── Permission Logs → view permission logs
 *   ── Industry Menus (dynamic) ──
 *   Ask Me (LLM)
 *   View Configuration
 *   Report Center
 */
const ClientPortalSidebar = ({
  activeTab,
  setActiveTab,
  clientConfig,
  onBackToOperator,
}) => {
  const tenantType = (clientConfig?.tenant_type || 'standalone').toLowerCase();
  const productType = clientConfig?.product_type || '';
  const isTN = isTNProcurement(productType);
  const isIW = isIWPfas(productType);

  // Dynamic analytics label resolution
  const [analyticsLabel, setAnalyticsLabel] = useState(null);
  const [analyticsVisible, setAnalyticsVisible] = useState(false);

  useEffect(() => {
    if (!productType) return;

    // Try client-side fallback first from TERMINOLOGY_DEFAULTS
    const fallbackShortName = TERMINOLOGY_DEFAULTS[productType];
    if (fallbackShortName) {
      setAnalyticsLabel(`${fallbackShortName} Analytics`);
      setAnalyticsVisible(true);
    }

    // Attempt to fetch from API for authoritative label
    const fetchLabel = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/v1/analytics/label?product_type=${encodeURIComponent(productType)}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.visible === false) {
            setAnalyticsVisible(false);
            setAnalyticsLabel(null);
          } else if (data.label) {
            setAnalyticsLabel(data.label);
            setAnalyticsVisible(true);
          }
        }
        // If API fails, keep the fallback value (if any)
      } catch {
        // Network error — keep fallback from TERMINOLOGY_DEFAULTS
      }
    };
    fetchLabel();
  }, [productType]);

  // Core navigation items under Intelligence Dashboard
  // IDs must match the tab IDs inside ClientApplication:
  //   overview → Overview tab, genai → LLM Ops tab, agentic → Comm-AI tab
  const coreItems = [
    { id: 'overview', icon: '🏠', label: 'Intelligence Governance' },
    { id: 'genai', icon: '💬', label: (() => {
      const pt = productType.toLowerCase();
      if (pt.includes('china-us') || pt.includes('strategic-intel')) return 'AI Ops';
      if (pt.includes('procurement')) return 'AI Ops';
      return 'AI Ops';
    })() },
    { id: 'agentic', icon: '🐙', label: (() => {
      const pt = productType.toLowerCase();
      if (pt.includes('china-us') || pt.includes('strategic-intel')) return 'Executive Orchestration';
      if (pt.includes('procurement')) return 'Workflows';
      if (pt.includes('water') || pt.includes('pfas')) return 'Executive Orchestration';
      if (pt.includes('bank') || pt.includes('financial')) return 'Risk Alerts';
      return 'Comm-AI';
    })() },
    { id: 'permissions', icon: '🔐', label: 'Permission Logs' },
  ];

  // Bottom utility menus
  const utilityMenus = [
    { id: 'view-config', icon: '⚙️', label: 'View Configuration' },
    { id: 'reports', icon: '📈', label: 'Report Center' },
  ];

  if (tenantType === 'parent') {
    utilityMenus.push({ id: 'chain', icon: '🏗️', label: 'Chain Overview' });
  }

  // Client management menus (subscriber self-service)
  const clientManagementMenus = [
    { id: 'view-client-details', icon: '🔍', label: 'View Client Details' },
    { id: 'update-client-details', icon: '✏️', label: 'Update Client Details' },
  ];

  // Determine if this client has a real database connected
  const resolvedDbConfig = clientConfig?.db_config || resolveDbConfig(null, clientConfig?.product_type);
  const dbStatus = getDbStatus(resolvedDbConfig, clientConfig?.product_type);
  const dotColor = dbStatus.color;
  const dotLabel = dbStatus.label;

  return (
    <aside className="sidebar client-portal-sidebar">
      <div className="logo">
        <h2>
          <span title={dbStatus.tooltip} style={{ color: dotColor, cursor: 'help', marginRight: '6px', fontSize: '12px' }}>●</span>
          {clientConfig?.company_name || 'Client Portal'}
        </h2>
        <span className="client-badge portal-badge">
          {tenantType === 'parent' ? '🏗️ Chain Owner' :
           tenantType === 'sub_client' ? '📍 Location' : '🏢 Portal'}
        </span>
      </div>

      <nav className="nav">
        {/* Intelligence Dashboard header + core sub-items */}
        <div className="nav-divider" aria-hidden="true">
          <span className="divider-label">📊 Intelligence Dashboard</span>
        </div>
        {coreItems.map(item => (
          <button
            key={item.id}
            className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => setActiveTab(item.id)}
            aria-current={activeTab === item.id ? 'page' : undefined}
          >
            <span className="icon">{item.icon}</span>
            <span className="label">{item.label}</span>
          </button>
        ))}

        {/* Product Analytics — dynamic label from product type */}
        {analyticsVisible && analyticsLabel && (
          <button
            key="product-analytics"
            className={`nav-item ${activeTab === 'product-analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('product-analytics')}
            aria-current={activeTab === 'product-analytics' ? 'page' : undefined}
          >
            <span className="icon">📊</span>
            <span className="label">{analyticsLabel}</span>
          </button>
        )}

        {/* TN-IPDS Procurement Intelligence menu — shown only for TN Govt product type */}
        {isTN && (
          <>
            <div className="nav-divider" aria-hidden="true">
              <span className="divider-label">🏛️ TN Procurement Intelligence</span>
            </div>
            {[
              { id: 'tn-tenders',           icon: '📋', label: 'Tenders' },
              { id: 'tn-vendors',           icon: '🏢', label: 'Vendors' },
              { id: 'tn-policy-qa',         icon: '⚖️', label: 'Policy Q&A' },
              { id: 'tn-risk-cases',        icon: '🚨', label: 'Risk Cases' },
              { id: 'tn-workflow-queue',    icon: '🔄', label: 'Workflow Queue' },
              { id: 'tn-district-dashboard',icon: '🗺️', label: 'District Dashboard' },
              { id: 'tn-cm-directives',     icon: '📜', label: 'CM Directives' },
              { id: 'tn-audit-trail',       icon: '🔍', label: 'Audit Trail' },
            ].map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
                aria-current={activeTab === item.id ? 'page' : undefined}
              >
                <span className="icon">{item.icon}</span>
                <span className="label">{item.label}</span>
              </button>
            ))}
          </>
        )}

        {/* IW-PFAS Intelligence menu — shown only for IndustrialWater-PFAS-Intelligence.Type-2 */}
        {isIW && (
          <>
            <div className="nav-divider" aria-hidden="true">
              <span className="divider-label">💧 Industrial Water Intelligence</span>
            </div>
            {[
              { id: 'iw-overview',             icon: '📊', label: 'Overview' },
              { id: 'iw-sites',                icon: '🏭', label: 'Sites' },
              { id: 'iw-membrane-assets',      icon: '🔬', label: 'Membrane Assets' },
              { id: 'iw-water-quality',        icon: '🧪', label: 'Water Quality' },
              { id: 'iw-compliance-watchlist',  icon: '⚖️', label: 'Compliance Watchlist' },
              { id: 'iw-risk-cases',           icon: '🚨', label: 'Risk Cases' },
              { id: 'iw-maintenance',          icon: '🔧', label: 'Maintenance' },
              { id: 'iw-inventory',            icon: '📦', label: 'Inventory & Suppliers' },
              { id: 'iw-customers',            icon: '👥', label: 'Customers' },
              { id: 'iw-policy-qa',            icon: '💬', label: 'Policy Q&A' },
              { id: 'iw-executive-decisions',  icon: '📋', label: 'Executive Decisions' },
              { id: 'iw-audit-trail',          icon: '🔍', label: 'Audit Trail' },
            ].map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
                aria-current={activeTab === item.id ? 'page' : undefined}
              >
                <span className="icon">{item.icon}</span>
                <span className="label">{item.label}</span>
              </button>
            ))}
          </>
        )}

        {/* GeoPulse Intelligence menu — shown only for China-US-Strategic-Intel product type */}
        {(productType.includes('China-US') || productType.includes('Strategic-Intel')) && (
          <>
            <div className="nav-divider" aria-hidden="true">
              <span className="divider-label">🌏 GeoPulse Intelligence</span>
            </div>
            {[
              { id: 'gp-daily-pulse',       icon: '📡', label: 'Daily Pulse' },
              { id: 'gp-corporate-exposure', icon: '🏢', label: 'Corporate Exposure' },
              { id: 'gp-sector-scores',     icon: '📊', label: 'Sector Scores' },
              { id: 'gp-forecasts',         icon: '🔮', label: 'Forecasts' },
              { id: 'gp-tariff-countdown',  icon: '⏱️', label: 'Tariff Countdown' },
              { id: 'gp-export-controls',   icon: '🚫', label: 'Export Controls' },
              { id: 'gp-ceo-directives',    icon: '📜', label: 'CEO Directives' },
              { id: 'gp-portfolio-signals', icon: '💼', label: 'Portfolio Signals' },
            ].map(item => (
              <button
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
                aria-current={activeTab === item.id ? 'page' : undefined}
              >
                <span className="icon">{item.icon}</span>
                <span className="label">{item.label}</span>
              </button>
            ))}
          </>
        )}

        {/* Utility menus */}
        {utilityMenus.map(item => (
          <button
            key={item.id}
            className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => setActiveTab(item.id)}
            aria-current={activeTab === item.id ? 'page' : undefined}
          >
            <span className="icon">{item.icon}</span>
            <span className="label">{item.label}</span>
          </button>
        ))}

        {/* Client Management (subscriber self-service) */}
        <div className="nav-divider" aria-hidden="true">
          <span className="divider-label">📋 Client Management</span>
        </div>
        {clientManagementMenus.map(item => (
          <button
            key={item.id}
            className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
            onClick={() => setActiveTab(item.id)}
            aria-current={activeTab === item.id ? 'page' : undefined}
          >
            <span className="icon">{item.icon}</span>
            <span className="label">{item.label}</span>
          </button>
        ))}
      </nav>

      <div className="client-info">
        <div className="client-info-row">
          <span className="info-label">Client ID</span>
          <span className="info-value" style={{ fontFamily: 'monospace', fontSize: '11px' }}>
            {clientConfig?.client_id || '—'}
          </span>
        </div>
        <div className="client-info-row">
          <span className="info-label">Plan</span>
          <span className="info-value">{clientConfig?.plan || 'starter'}</span>
        </div>
        <button
          className="nav-item onboard-another"
          onClick={onBackToOperator}
          aria-label="Return to operator console"
        >
          <span className="icon">🔙</span>
          <span className="label">{clientConfig?.tenant_role === 'subscriber' ? 'Back to Home' : 'Back to Operator Console'}</span>
        </button>
      </div>

      <div className="sidebar-footer">
        <button
          className="nav-item logout-btn"
          onClick={handleLogout}
          aria-label="Logout"
        >
          <span className="icon">🚪</span>
          <span className="label">Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default ClientPortalSidebar;
